import React from 'react';
import I18n from 'i18n-js';
import { ChildProps } from 'react-apollo';
import gql from 'graphql-tag';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import CategoryFlyoutHeader from '../../new_header/category_flyout_header';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { RCIcon } from '@reverbdotcom/cadence/components';
import { HeartEmptyIcon, /* ReverbLogoIcon, */ FeedAltIcon } from '@reverbdotcom/cadence/icons/react';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import componentUrlHelpers, { Paths } from '../../shared/url_helpers';
import baseUrlHelpers from '../../../shared/url_helpers';

import SiteSearch from '../site_search/reverb_site_search';
import LogIn from './login';
import ReverbHeaderUserBadges from './reverb_header_user_badges';
import ReverbHeaderCartBadge, { ReverbHeaderCartBadgeFragment } from './reverb_header_cart_badge';
import ReverbHeaderDesktopMenu from './reverb_header_desktop_menu';
import ReverbHeaderMobileMenu from './reverb_header_mobile_menu';
import ReverbHeaderUserMenuFragment from './reverb_header_user_menu_fragment';
import { CoreReverbHeader } from '../../../schemas/rql';
import { inExperiment } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import classNames from 'classnames';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

import ReverbLogoPride from '../../../../images/reverb-logo-pride.svg';

interface IProps extends IUserContext {
  loginComponent?: React.ReactNode; // used to pass in an oauth login component from sandbox layout.tsx
}

export function ReverbHeader({ loginComponent, data, user }: ChildProps<IProps, CoreReverbHeader.Query>) {
  const COMPONENT_NAME = 'ReverbHeader';
  const [activeDropdownKey, setActiveDropdownKey] = React.useState();
  const { loggedOut } = user;
  const me = data?.me;

  const { query } = useURL();
  const inFavoritesExperiment = inExperiment(user, experiments.FAVORITES);
  const inElevatedFavoritesNavExperiment = inExperiment(user, experiments.FAVORITES_ELEVATED_NAVIGATION);

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: COMPONENT_NAME,
    experiments: formatExperimentsForEventAttributes(
      user,
      [
        experiments.FAVORITES_ELEVATED_NAVIGATION,
      ],
    ),
  }, !data?.loading);

  return (
    <>
      <div className="reverb-header__main">
        <div className="reverb-header__inner">
          <nav aria-label={I18n.t('discovery.reverbHeader.navigationRole')}>
            {inElevatedFavoritesNavExperiment ? (
              <ul className="reverb-header__nav__links">
                <ReverbHeaderMobileMenu me={me} />
                <li className="reverb-header__show-on-mobile">
                  <CoreLink
                    to="/"
                    className="reverb-header__logo"
                    aria-label={I18n.t('discovery.reverbHeader.reverbLogo')}
                  >
                    <img src={ReverbLogoPride} className="reverb-header__pride-img" alt="" />
                    {/* <ReverbLogoIcon /> */}
                  </CoreLink>
                </li>
              </ul>
            ) : (
              <CoreLink
                to="/"
                className="reverb-header__logo"
                aria-label={I18n.t('discovery.reverbHeader.reverbLogo')}
              >
                <img src={ReverbLogoPride} className="reverb-header__pride-img" alt="" />
                {/* <ReverbLogoIcon /> */}
              </CoreLink>
            )}
            <div className={classNames(
              'reverb-header__nav',
              {
                'reverb-header__nav--main': !inElevatedFavoritesNavExperiment,
                'reverb-header__nav--experimental-main': inElevatedFavoritesNavExperiment,
              },
            )}>
              <div className="reverb-header__nav__search">
                <SiteSearch defaultValue={query.query} />
              </div>
            </div>
            <nav
              aria-label={I18n.t('discovery.reverbHeader.myReverb')}
              role="navigation"
              className={classNames(
                'reverb-header__nav reverb-header__nav--account',
                {
                  'reverb-header__nav--account-center': !inElevatedFavoritesNavExperiment,
                },
              )}
            >
              <ul className="reverb-header__nav__links">
                <li className="reverb-header__show-on-mobile">
                  <div className={classNames(
                    'mobile-d-none',
                    {
                      'tablet-d-none': inElevatedFavoritesNavExperiment,
                    },
                  )}>
                    <CoreLink
                      className="reverb-header__nav__link reverb-header__nav__link--sell-button"
                      to={componentUrlHelpers.sellFormSearchPath({})}
                      clickEvent={{
                        componentName: COMPONENT_NAME,
                        entityId: componentUrlHelpers.sellFormSearchPath({}),
                        entityType: 'url',
                        eventName: MParticleEventName.ClickedComponent,
                      }}
                    >
                      {I18n.t('discovery.reverbHeader.sellCTAFull')}
                    </CoreLink>
                  </div>
                  <div className={classNames(
                    'd-none',
                    {
                      'mobile-d-block': !inElevatedFavoritesNavExperiment,
                    },
                  )}>
                    <CoreLink
                      className="reverb-header__nav__link reverb-header__nav__link--sell-button"
                      to={componentUrlHelpers.sellFormSearchPath({})}
                      clickEvent={{
                        componentName: COMPONENT_NAME,
                        entityId: componentUrlHelpers.sellFormSearchPath({}),
                        entityType: 'url',
                        eventName: MParticleEventName.ClickedComponent,
                      }}
                    >
                      {I18n.t('discovery.reverbHeader.sellCTAMobile')}
                    </CoreLink>
                  </div>
                </li>
                {inFavoritesExperiment &&
                  <li className={classNames(
                    {
                      'reverb-header__badges-show-on-mobile reverb-header__favorites-heart': inElevatedFavoritesNavExperiment,
                    },
                  )}>
                    <CoreLink
                      className="reverb-header__nav__link"
                      to={Paths.myFavorites.expand({})}
                      title={I18n.t('discovery.reverbHeader.favorites')}
                    >
                      <div className="reverb-header__nav__link__icon">
                        <RCIcon svgComponent={HeartEmptyIcon} />
                      </div>
                      <div className="reverb-header__nav__link__label">
                        {I18n.t('discovery.reverbHeader.favorites')}
                      </div>
                    </CoreLink>
                  </li>
                }
                {!inFavoritesExperiment &&
                  <>
                    <li>
                      <CoreLink
                        className="reverb-header__nav__link"
                        to={baseUrlHelpers.watchedProductsPath}
                        title={I18n.t('discovery.reverbHeader.watchListTooltip')}
                      >
                        <div className="reverb-header__nav__link__icon">
                          <RCIcon svgComponent={HeartEmptyIcon} />
                        </div>
                        <div className="reverb-header__nav__link__label">
                          {I18n.t('discovery.reverbHeader.watchList')}
                        </div>
                      </CoreLink>
                    </li>
                    <li>
                      <CoreLink
                        className="reverb-header__nav__link"
                        to={baseUrlHelpers.myFeedPath}
                      >
                        <div className="reverb-header__nav__link__icon">
                          <RCIcon svgComponent={FeedAltIcon} />
                        </div>
                        <div className="reverb-header__nav__link__label">
                          {I18n.t('discovery.reverbHeader.myFeed')}
                        </div>
                      </CoreLink>
                    </li>
                  </>
                }
                <li className={classNames(
                  'reverb-header__nav__link__auto-width',
                  {
                    'reverb-header__show-on-mobile': !inElevatedFavoritesNavExperiment,
                    'reverb-header__badges-show-on-mobile': inElevatedFavoritesNavExperiment,
                  },
                )}>
                  <ReverbHeaderCartBadge
                    activeDropdownKey={activeDropdownKey}
                    setActiveDropdownKey={setActiveDropdownKey}
                    me={me}
                  />
                </li>
                {!loggedOut && (
                  <li className={classNames(
                    'reverb-header__nav__link__auto-width',
                    {
                      'reverb-header__show-on-mobile': !inElevatedFavoritesNavExperiment,
                      'reverb-header__badges-show-on-mobile': inElevatedFavoritesNavExperiment,
                    },
                  )}>
                    <ReverbHeaderUserBadges
                      activeDropdownKey={activeDropdownKey}
                      setActiveDropdownKey={setActiveDropdownKey}
                    />
                  </li>
                )}

                {loggedOut && !loginComponent && <LogIn />}
                {loggedOut && loginComponent}
                {!loggedOut && (
                  <li className="reverb-header__nav__link__auto-width">
                    <ReverbHeaderDesktopMenu
                      activeDropdownKey={activeDropdownKey}
                      setActiveDropdownKey={setActiveDropdownKey}
                      me={me}
                    />
                  </li>
                )}
                {!inElevatedFavoritesNavExperiment &&
                  <ReverbHeaderMobileMenu me={me} />
                }
              </ul>
            </nav>
          </nav>
        </div>
      </div>
      <div className="reverb-header__links">
        <CategoryFlyoutHeader />
      </div>
    </>
  );
}

const withQuery = withGraphql<IProps, CoreReverbHeader.Query>(
  gql`
    query Core_ReverbHeader {
      me {
        uuid
        ...ReverbHeaderCartBadgeData
        ...ReverbHeaderUserMenuData
      }
    }
    ${ReverbHeaderCartBadgeFragment}
    ${ReverbHeaderUserMenuFragment}
  `,
  {
    skip: (ownProps) => (ownProps.user.loggedOut),
    options: {
      ssr: false,
    },
  },
);

export default withUserContext(withQuery(ReverbHeader));
